import { useState } from "react";
import { fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  useApi, post, put, Panel, PageHead, Stat, QuickForm, DataTable, StatusBadge, Tabs,
} from "@/crm/ui";

const TABS = ["Open", "In Progress", "Completed", "All"];

export default function Tasks() {
  const [tab, setTab] = useState("Open");
  const [form, setForm] = useState({});
  const status = tab === "All" ? "all" : tab;
  const list = useApi(`/crm/tasks?status=${encodeURIComponent(status)}`, [tab]);
  const all = useApi("/crm/tasks?status=all");
  const leads = useApi("/crm/leads");
  const options = useApi("/crm/options");
  const rows = list.data || [];
  const every = all.data || [];
  const opt = (r, id, name) => (r || []).map((x) => ({ value: x[id], label: `${x[name]} (${x[id]})` }));

  const setStatus = async (row, next) => {
    const r = await put(`/crm/tasks/${row.task_id}`, { status: next }, `Task ${next}`);
    if (r) { list.reload(); all.reload(); }
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <PageHead eyebrow="CRM" title="Tasks"
                subtitle="Assign work to any user with a due date and priority. Tasks can be linked to a lead or a bank application and are fully audited." />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Open" value={every.filter((t) => t.status === "Open").length} testid="task-open" />
        <Stat label="In Progress" value={every.filter((t) => t.status === "In Progress").length} testid="task-progress" />
        <Stat label="Overdue" value={every.filter((t) => t.overdue).length} testid="task-overdue" />
        <Stat label="Completed" value={every.filter((t) => t.status === "Completed").length} testid="task-completed" />
      </div>

      <Panel title="Create task" testid="create-task-panel">
        <QuickForm testid="task-form" values={form} submitLabel="Create task"
                   onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
                   fields={[
                     { key: "title", label: "Title" },
                     { key: "task_type", label: "Type", options: ["General", "Document Collection", "Bank Coordination", "Credit Query", "Disbursement", "Payout", "Compliance"] },
                     { key: "assigned_to", label: "Assign to (user email)" },
                     { key: "employee_id", label: "Employee", options: opt(options.data?.employees, "employee_id", "employee_name") },
                     { key: "partner_id", label: "Partner", options: opt(options.data?.partners, "partner_id", "partner_name") },
                     { key: "lead_id", label: "Related Lead", options: opt(leads.data, "lead_id", "customer_name") },
                     { key: "due_date", label: "Due Date", type: "date" },
                     { key: "priority", label: "Priority", options: ["Low", "Medium", "High", "Urgent"] },
                     { key: "description", label: "Description" },
                   ]}
                   onSubmit={async () => {
                     const r = await post("/crm/tasks", form, "Task created");
                     if (r) { setForm({}); list.reload(); all.reload(); }
                   }} />
      </Panel>

      <Tabs tabs={TABS} active={tab} onChange={setTab} testid="task-tabs" />
      <Panel title={`${tab} tasks (${rows.length})`} testid="tasks-panel">
        <DataTable testid="tasks-table" rows={rows} empty="No tasks in this view."
                   columns={[{ key: "task_id", label: "Task ID" }, { key: "title", label: "Title" },
                     { key: "task_type", label: "Type" }, { key: "assigned_to", label: "Assigned To" },
                     { key: "employee_id", label: "Employee" }, { key: "partner_id", label: "Partner" },
                     { key: "lead_id", label: "Lead" },
                     { key: "due_date", label: "Due", render: (r) => `${fmtDate(r.due_date)}${r.overdue ? " ⚠" : ""}` },
                     { key: "priority", label: "Priority", render: (r) => <StatusBadge value={r.priority} /> },
                     { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                     { key: "actions", label: "Actions", render: (r) => (
                       <span className="flex gap-1">
                         {["In Progress", "Completed", "Cancelled"].filter((s) => s !== r.status).map((s) => (
                           <Button key={s} size="sm" variant="outline" className="bg-white text-[10px]"
                                   data-testid={`task-${s.toLowerCase().replace(/ /g, "-")}-${r.task_id}`}
                                   onClick={() => setStatus(r, s)}>{s}</Button>))}
                       </span>) }]} />
      </Panel>
    </div>
  );
}
